import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import PhotoCarousel from "./PhotoCarousel";
import { CloseOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import Spinner from "../Spinner";
import { EmptyState, ErrorState } from "../states";
import {
  formatAddress,
  formatAgentName,
  formatCityLine,
  formatLongDate,
  formatOpenHouse,
  formatPrice,
  formatPricePerSqFt,
  formatPropertyCategory,
  formatPropertyType,
} from "./listingDisplay";
import { goLiveLabel } from "../../utils/goLive";
import { GET_UPCOMING_LISTINGS } from "../../graphql/listingQueries";
import { GET_LISTING_BUYER_MATCHES_FOR_COMPANY } from "../../graphql/buyerNeedsQueries";
import {
  ALL_AGENTS,
  SIBLING_FETCH_LIMIT,
  UPCOMING_WINDOW_DAYS,
} from "../../constants/upcomingListingsConstants";

const Key = ({ children }) => (
  <kbd className="rounded-sm border border-light_gray bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-secondary">
    {children}
  </kbd>
);

/**
 * How many of the office's buyers this listing suits. A count, not a list — the
 * slide is for talking to, and the individual buyers are a click away on the
 * detail view.
 */
const MatchLine = ({ listingId }) => {
  const { data } = useQuery(GET_LISTING_BUYER_MATCHES_FOR_COMPANY, {
    variables: { listingId },
    fetchPolicy: "cache-and-network",
    skip: !listingId,
    // A broker without office-wide access simply gets no line.
    onError: () => {},
  });

  const count = data?.getCompanyListingBuyerMatches?.length ?? 0;
  if (!count) return null;

  return (
    <div className="mt-2 border-t border-light_gray pt-2 text-sm text-secondary">
      <b className="text-dark_gray">Buyer Matches</b> {count}{" "}
      {count === 1 ? "buyer" : "buyers"} in this office match this listing
    </div>
  );
};

const Spec = ({ label, value }) =>
  value ? (
    <div>
      <div className="text-xl font-black text-dark_gray">
        {/* A year is a label, not a quantity. */}
        {label === "Built" ? value : value.toLocaleString()}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
    </div>
  ) : null;

/**
 * Meeting Mode — one listing filling the screen, for a broker sharing their
 * screen on Zoom or Teams while they run the upcoming-listings part of a sales
 * meeting.
 *
 * It loads the whole filtered set rather than the page the list happened to have
 * scrolled to: a presentation that stops after twelve listings because nobody
 * pressed "Load more" would fail in the room.
 */
/**
 * One listing's slide. Neighbouring slides are mounted so the track can animate
 * between them, but only the active one carries the live pieces — the photo
 * counter and the buyer-match count — so there is exactly one of each on screen
 * and one query in flight.
 */
// A horizontal drag shorter than this is a tap or a wobble, not a swipe.
const SWIPE_MIN_PX = 40;

const Slide = ({ listing, active, photoIndex, failed, onFailed, onSwipe }) => {
  const touchX = useRef(null);
  const badge = [goLiveLabel(listing.expiration), formatLongDate(listing.expiration)]
    .filter(Boolean)
    .join(" · ");
  const cityLine = [
    formatCityLine(listing),
    formatPropertyCategory(listing.propertyCategory) ||
      formatPropertyType(listing.propertyType),
  ]
    .filter(Boolean)
    .join(" · ");
  const price = formatPrice(listing.price);
  const perSqFt = formatPricePerSqFt(listing);
  const photos = listing.imageURLs ?? [];
  const amenities = (listing.amenities ?? []).filter((a) => a?.title);
  const openHouses = (listing.openHouses ?? []).filter((oh) => oh?.startTime);
  const footer = [formatAgentName(listing.user), listing.office?.name]
    .filter(Boolean)
    .join(" · ");

  // Phones have no keyboard and no room for a filmstrip: photos follow a swipe.
  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) >= SWIPE_MIN_PX) onSwipe(dx < 0 ? 1 : -1);
  };

  // Phones stack the photo above the details in one scrolling column;
  // from md the two sit side by side as before.
  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto md:flex-row md:items-stretch md:gap-5">
      <div
        data-testid="photo-frame"
        onTouchStart={active ? onTouchStart : undefined}
        onTouchEnd={active ? onTouchEnd : undefined}
        className="relative h-60 shrink-0 overflow-hidden rounded-lg bg-light_gray md:h-auto md:min-h-[300px] md:flex-[1.4]"
      >
        <PhotoCarousel
          photos={photos}
          index={active ? photoIndex : 0}
          failed={failed}
          onFailed={onFailed}
          emptyLabel="No photos for this listing"
        />
        {active && photos.length > 1 && (
          <>
            <span className="absolute bottom-3 right-3 rounded-sm bg-dark_gray/85 px-2 py-1 text-xs font-bold text-white">
              {photoIndex + 1} / {photos.length}
            </span>
            {/* Where the swipe is; the filmstrip does this job from md up. */}
            <div
              aria-hidden="true"
              className="absolute inset-x-0 bottom-3.5 flex justify-center gap-1.5 md:hidden"
            >
              {photos.map((src, i) => (
                <span
                  key={`${src}-${i}`}
                  className={`h-2 w-2 rounded-full ${
                    i === photoIndex ? "bg-white" : "bg-white/55"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="min-w-0 md:flex-1">
        {badge && (
          <span className="inline-block rounded-sm bg-blue px-2.5 py-1 text-xs font-extrabold tracking-wide text-white">
            {badge}
          </span>
        )}

        <h2 className="mb-0.5 mt-3 text-2xl font-black text-dark_gray md:text-3xl">
          {formatAddress(listing)}
        </h2>
        {cityLine && <div className="text-sm text-gray-400">{cityLine}</div>}

        {price && (
          <div className="my-3 text-3xl font-black text-primary_dark md:text-4xl">
            {price}
            {perSqFt && (
              <span className="ml-2 text-base font-semibold text-gray-400">
                · {perSqFt}
              </span>
            )}
          </div>
        )}

        {/* Phones: one ruled row of five; md up: the loose wrap as before. */}
        <div className="mb-3 grid grid-cols-5 gap-1 border-y border-light_gray py-2.5 text-center md:flex md:flex-wrap md:gap-6 md:border-0 md:py-0 md:text-left">
          <Spec label="Beds" value={listing.beds} />
          <Spec label="Baths" value={listing.baths} />
          <Spec label="Sq Ft" value={listing.sqFt} />
          <Spec label="Acres" value={listing.acreage} />
          <Spec label="Built" value={listing.yearBuilt} />
        </div>

        {amenities.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {amenities.map((a) => (
              <span
                key={a._id}
                className="rounded-sm bg-light_green px-2 py-1 text-xs text-secondary"
              >
                {a.title}
              </span>
            ))}
          </div>
        )}

        {openHouses.length > 0 && (
          <div className="border-t border-light_gray pt-2 text-sm text-secondary">
            <b className="text-dark_gray">Open House</b>{" "}
            {openHouses.map((oh, i) => (
              <span key={i}>
                {i > 0 && " · "}
                {formatOpenHouse(oh)}
                {oh.needHost && <span className="text-red"> (host needed)</span>}
              </span>
            ))}
          </div>
        )}

        {active && <MatchLine listingId={listing._id} />}

        {listing.notes && (
          <p className="mt-2 border-t border-light_gray pt-2 text-sm text-secondary">
            {listing.notes}
          </p>
        )}

        {footer && <div className="mt-2 pt-1 text-xs text-gray-400">{footer}</div>}
      </div>
    </div>
  );
};

const MeetingMode = ({ companyId, agentId, agentLabel, sortBy, startId, onExit }) => {
  const [index, setIndex] = useState(null);
  const [photo, setPhoto] = useState(0);
  const [failed, setFailed] = useState({});

  const { data, loading, error, refetch } = useQuery(GET_UPCOMING_LISTINGS, {
    variables: {
      companyId,
      listingInput: {
        upcomingOnly: true,
        upcomingWithinDays: UPCOMING_WINDOW_DAYS,
        ...(agentId !== ALL_AGENTS && { agentId }),
      },
      pagination: { page: 1, limit: SIBLING_FETCH_LIMIT },
      sortBy,
    },
    fetchPolicy: "cache-and-network",
  });

  const listings = useMemo(
    () => data?.getCompanyListings?.listing ?? [],
    [data]
  );

  // Start on the listing the broker clicked Present from, not always the first.
  useEffect(() => {
    if (index !== null || !listings.length) return;
    const at = startId ? listings.findIndex((l) => l._id === startId) : 0;
    setIndex(at === -1 ? 0 : at);
  }, [listings, startId, index]);

  const at = index ?? 0;
  const listing = listings[at];
  const photos = listing?.imageURLs ?? [];

  useEffect(() => {
    setPhoto(0);
    setFailed({});
  }, [listing?._id]);

  // Presenting is a keyboard activity — the broker is talking, not aiming a
  // mouse. Left/right walk the listings, up/down the photos of one listing.
  useEffect(() => {
    const onKeyDown = (event) => {
      const total = listings.length;
      switch (event.key) {
        case "Escape":
          onExit();
          break;
        case "ArrowRight":
          if (total) setIndex((i) => Math.min((i ?? 0) + 1, total - 1));
          break;
        case "ArrowLeft":
          if (total) setIndex((i) => Math.max((i ?? 0) - 1, 0));
          break;
        case "ArrowDown":
        case "ArrowUp":
          if (photos.length > 1) {
            const delta = event.key === "ArrowDown" ? 1 : -1;
            setPhoto((p) => Math.min(Math.max(p + delta, 0), photos.length - 1));
          }
          break;
        default:
          return;
      }
      event.preventDefault();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [listings.length, photos.length, onExit]);

  // The page behind must not scroll while the overlay is up.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const shell = (children) => (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Meeting Mode"
      className="fixed inset-0 z-50 flex flex-col bg-gray-50"
    >
      <div className="flex items-center justify-between gap-3 border-b border-light_gray bg-white py-1 pl-4 pr-1 text-sm md:px-5 md:py-3">
        <div className="min-w-0">
          <span className="font-extrabold text-dark_gray">Upcoming Listings</span>
          <span className="text-gray-300"> | </span>
          <span className="font-extrabold text-primary_dark">
            {agentLabel ?? "All agents"}
          </span>
          {/* Phones show the position in the bar under the slide instead. */}
          {listings.length > 0 && (
            <span className="hidden text-gray-400 md:inline">
              {" "}
              · {at + 1} of {listings.length}
            </span>
          )}
        </div>

        {/* Key hints only from md: a phone has no keyboard to press them on. */}
        <div className="flex shrink-0 items-center gap-3 text-xs text-gray-400">
          <span className="hidden md:inline">
            <Key>←</Key> <Key>→</Key> listings
          </span>
          {photos.length > 1 && (
            <span className="hidden md:inline">
              <Key>↑</Key> <Key>↓</Key> photos
            </span>
          )}
          <span className="hidden md:inline">
            <Key>Esc</Key> exit
          </span>
          <button
            type="button"
            aria-label="Exit Meeting Mode"
            onClick={onExit}
            className="flex h-11 w-11 items-center justify-center text-gray-400 hover:text-dark_gray md:ml-1 md:h-auto md:w-auto"
          >
            <CloseOutlined />
          </button>
        </div>
      </div>
      {children}
    </div>
  );

  if (loading && !listings.length) {
    return shell(
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error && !listings.length) {
    return shell(
      <div className="flex flex-1 items-center justify-center">
        <ErrorState onRetry={() => refetch()} />
      </div>
    );
  }

  if (!listing) {
    return shell(
      <div className="flex flex-1 items-center justify-center">
        <EmptyState
          title="Nothing to present"
          description={`No listings go live on MLS in the next ${UPCOMING_WINDOW_DAYS} days.`}
          actionLabel="Close"
          onAction={onExit}
        />
      </div>
    );
  }

  // One pair of listing buttons for every width: a labelled bar under the slide
  // on phones, round arrows floating over the slide's sides from md.
  const NAV_BUTTON_CLS =
    "flex h-11 min-w-[104px] items-center justify-center gap-1.5 rounded-lg px-3.5 text-[15px] transition-opacity disabled:pointer-events-none disabled:opacity-25 md:pointer-events-auto md:w-11 md:min-w-0 md:rounded-full md:border-0 md:bg-primary md:p-0 md:text-white md:shadow";

  // Same guard as the arrow keys: with 0–1 photos there is nowhere to move,
  // and clamping to `photos.length - 1` would leave the index at -1.
  const move = (delta) => {
    if (photos.length < 2) return;
    setPhoto((p) => Math.min(Math.max(p + delta, 0), photos.length - 1));
  };

  return shell(
    <>
      <div className="relative flex flex-1 flex-col overflow-hidden">
        {/* Listings ride the same kind of track the photos do: the slide being
            left shrinks as it goes and the one arriving grows into place, so a
            room watching the shared screen sees the move rather than a swap.
            Only the neighbours are mounted — the rest of the set is an empty
            box of the same width, which keeps a long meeting cheap. */}
        <div
          data-testid="slide-track"
          className="flex min-h-0 flex-1 transition-transform duration-450 ease-swift motion-reduce:transition-none"
          style={{ transform: `translateX(-${at * 100}%)` }}
        >
          {listings.map((item, i) => {
            const isActive = i === at;
            return (
              <div
                key={item._id}
                aria-hidden={isActive ? undefined : "true"}
                className={`h-full w-full shrink-0 p-4 md:p-5 transition-transform duration-450 ease-swift motion-reduce:transition-none ${
                  isActive ? "scale-100" : "scale-[0.92]"
                }`}
              >
                {Math.abs(i - at) <= 1 && (
                  <Slide
                    listing={item}
                    active={isActive}
                    photoIndex={photo}
                    failed={failed}
                    onFailed={(src) => setFailed((f) => ({ ...f, [src]: true }))}
                    onSwipe={move}
                  />
                )}
              </div>
            );
          })}
        </div>

        <nav
          aria-label="Listings"
          className="flex shrink-0 items-center justify-between gap-2 border-t border-light_gray bg-white px-4 pb-4 pt-2.5 md:pointer-events-none md:absolute md:inset-x-4 md:top-1/2 md:z-10 md:-translate-y-1/2 md:border-0 md:bg-transparent md:p-0"
        >
          <button
            type="button"
            aria-label="Previous listing"
            disabled={at === 0}
            onClick={() => setIndex(at - 1)}
            className={`${NAV_BUTTON_CLS} border border-gray-300 bg-white font-semibold text-dark_gray`}
          >
            <LeftOutlined />
            <span className="md:hidden">Prev</span>
          </button>
          <span className="text-sm font-bold text-dark_gray md:hidden">
            {at + 1} of {listings.length}
          </span>
          <button
            type="button"
            aria-label="Next listing"
            disabled={at === listings.length - 1}
            onClick={() => setIndex(at + 1)}
            className={`${NAV_BUTTON_CLS} bg-primary font-bold text-dark_gray`}
          >
            <span className="md:hidden">Next</span>
            <RightOutlined />
          </button>
        </nav>
      </div>

      {/* The strip's row is always present from md up, even when there is
          nothing to put in it. Letting the photo frame grow on a listing with no
          photos makes the layout jump as the broker moves between slides.
          Phones swipe the photo instead (see Slide). */}
      <div className="hidden h-20 shrink-0 px-5 pb-4 md:block">
        {photos.length > 1 && (
          <div className="flex h-full gap-2 overflow-x-auto">
            {photos.map((src, i) => (
              <button
                key={`${src}-${i}`}
                type="button"
                aria-label={`Show photo ${i + 1}`}
                aria-current={i === photo ? "true" : undefined}
                onClick={() => setPhoto(i)}
                className={`h-full w-24 shrink-0 overflow-hidden rounded-md bg-light_gray ${
                  i === photo ? "outline outline-2 outline-primary" : "opacity-50 hover:opacity-100"
                }`}
              >
                <img
                  alt=""
                  src={src}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default MeetingMode;
